import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Mail } from "lucide-react";
import Shell from "@/components/Shell";
import Brandmark from "@/components/Brandmark";
import { useApp } from "@/lib/store";

export default function Auth() {
  const { requestLink, verify } = useApp();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");

  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(token ? "Verifying your link…" : null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let ignore = false;
    if (token) {
      verify(token)
        .then((d) => { if (!ignore) nav(d.user.role === "admin" ? "/admin" : "/navigate"); })
        .catch(() => { if (!ignore) { setStatus(null); setError("This link is invalid or has expired."); } });
    }
    return () => { ignore = true; };
    // eslint-disable-next-line
  }, [token]);

  const send = async () => {
    if (!email.trim()) return;
    setBusy(true); setError(null);
    try {
      const d = await requestLink(email.trim());
      setSent(true);
      if (d.dev_link) setDevLink(d.dev_link);
    } catch (e) { setError("Could not send the link. Check the email and try again."); }
    setBusy(false);
  };

  if (token) {
    return (
      <Shell>
        <div className="px-6 pt-28 text-center">
          {error ? (
            <>
              <h1 className="font-serif-display text-3xl">{error}</h1>
              <button data-testid="verify-retry" onClick={() => nav("/login")}
                className="mt-6 bg-[#1A1A1A] text-[#FDFBF7] rounded-full px-6 py-3.5 font-medium ss-tap ss-focus">
                Back to sign in
              </button>
            </>
          ) : (
            <p className="text-[#6B6B6B] font-mono-ss text-sm">{status}</p>
          )}
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="px-6 pt-12 pb-16">
        <button data-testid="auth-back" onClick={() => nav(-1)}
          className="inline-flex items-center gap-1 text-[13px] text-[#6B6B6B] hover:text-[#1A1A1A] ss-focus -ml-1">
          <ArrowLeft size={18} strokeWidth={1.5} /> Back
        </button>

        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-10">
          <Brandmark size={30} className="mb-3" />
          <h1 className="font-serif-display text-4xl leading-tight">Save your progress</h1>
          <p className="mt-3 text-[15px] text-[#474747] leading-relaxed">
            One check-in can help with this moment. A handful of completed check-ins may begin to reveal your pattern. Sign in with
            your email to keep your history across devices. No password needed.
          </p>

          {!sent ? (
            <div className="mt-8">
              <label className="text-xs font-semibold uppercase tracking-[0.15em] text-[#6B6B6B]">Email</label>
              <input
                data-testid="auth-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="you@example.com"
                className="mt-2 w-full ss-card p-4 text-[15px] ss-focus"
              />
              <button data-testid="auth-send" onClick={send} disabled={busy}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#1A1A1A] text-[#FDFBF7] rounded-full px-6 py-4 font-medium ss-tap ss-focus hover:-translate-y-0.5">
                <Mail size={17} strokeWidth={1.5} /> {busy ? "Sending…" : "Send my sign-in link"}
              </button>
              {error && <p className="mt-3 text-[13px] text-[#a33]">{error}</p>}
            </div>
          ) : (
            <div className="mt-8 ss-card p-5" data-testid="auth-sent">
              <p className="text-[15px]">Check your email for a sign-in link.</p>
              <p className="mt-2 text-[13px] text-[#6B6B6B]">
                It expires in 15 minutes. If you don't see it, check spam.
              </p>
              {devLink && (
                <button
                  data-testid="auth-dev-continue"
                  onClick={() => nav(devLink)}
                  className="mt-4 w-full border border-[#C9A227] text-[#8a6d10] rounded-full px-5 py-3 text-[14px] font-medium ss-tap ss-focus"
                >
                  Dev: continue with generated link
                </button>
              )}
            </div>
          )}

          <p className="mt-8 text-[13px] text-[#6B6B6B] leading-relaxed">
            Signal State ID remembers only what helps you navigate. You decide what remains true — you can
            export or permanently delete everything at any time.
          </p>
        </motion.div>
      </div>
    </Shell>
  );
}
